import { useId, useState, type JSX } from 'react';

import { toPersianDigits } from '@bookora/shared';
import { Button, ICON_NAMES, Icon, IconButton, TruncatedText, type IconName } from '@bookora/ui';

import type { GuideTipCtaResponse, GuideTipResponse } from '@/api/generated/model';

import { CARD, CTA, DISMISS_LABEL, READ_ONLY_REASON, WHY_LABEL } from './copy';
import { renderGuideText } from './guide-rich-text';
import { GuideTipMenu } from './guide-tip-menu';
import { plainGuideText, tipWhy, type HidingAction, type TipMenuAction } from './tip-model';
import { isRenderableCta, type GuideCtaRunner } from './use-guide-cta';
import './guide.css';

/**
 * One suggestion, drawn four ways:
 *
 *   featured  the dashboard card's lead tip: eyebrow, heading, its one-line
 *             reason («چون …», muted), the action as an ordinary button (ink
 *             primary stays the page's own main action), up to two secondary
 *             links, «چرا؟» for the longer body, and ⋯ / × at the top end
 *   compact   a dashboard row under it: icon, title over its reason, the CTA
 *             as a link, ⋯
 *   inline    one quiet row under a page's toolbar (`role="note"`): title over
 *             its reason, the CTA, ⋯ (snooze in place) and ×
 *   row       the hub's list: title, the full body, the CTA, ⋯
 *
 * The reason is the rule's `why` (usually the live count): a bare imperative
 * title is not a reason to act, and on compact and inline rows the body used
 * to be missing entirely.
 * Numbers and dates in a title or body are the server's typed values rendered
 * by the kit's primitives (`renderGuideText`), never bare digits. Tone is
 * colour-coded by tokens only: TIMELY = the coral accent chip, ATTENTION =
 * the warning family, INFO = neutral.
 */

export type TipVariant = 'featured' | 'compact' | 'inline' | 'row';

function iconOf(name: string): IconName {
  return (ICON_NAMES as readonly string[]).includes(name) ? (name as IconName) : 'sparkle';
}

/** «۱۲ روز تا روز مادر» — the occasion's countdown, or the category for any other tip. */
function Eyebrow({ tip }: { tip: GuideTipResponse }): JSX.Element {
  if (tip.occasion) {
    const text =
      tip.occasion.daysLeft <= 0
        ? CARD.occasionToday(tip.occasion.nameFa)
        : CARD.occasionChip(toPersianDigits(tip.occasion.daysLeft), tip.occasion.nameFa);
    return <span className="bk-guide-chip bk-guide-chip--timely">{text}</span>;
  }
  return (
    <span className={`bk-guide-eyebrow bk-guide-eyebrow--${tip.tone.toLowerCase()}`}>
      {tip.categoryLabelFa}
    </span>
  );
}

function CtaButton({
  tip,
  cta,
  runner,
  appearance,
  size = 'small',
  secondary = false,
}: {
  tip: GuideTipResponse;
  cta: GuideTipCtaResponse;
  runner: GuideCtaRunner;
  appearance: 'link' | 'default';
  size?: 'small' | 'middle';
  /** A secondary link goes somewhere; it is not an answer to the tip. */
  secondary?: boolean;
}): JSX.Element | null {
  if (!isRenderableCta(cta)) return null;
  const preparing = cta.kind === 'DRAFT' && runner.pendingItemKey === tip.itemKey;
  return (
    <Button
      type={appearance}
      size={appearance === 'link' ? 'small' : size}
      className={appearance === 'link' ? 'bk-guide-link' : undefined}
      loading={preparing}
      disabledReason={runner.reasonFor(cta)}
      onClick={() => runner.run(tip, cta, { secondary })}
    >
      {preparing ? CTA.preparingDraft : cta.label}
    </Button>
  );
}

export interface GuideTipCardProps {
  tip: GuideTipResponse;
  variant: TipVariant;
  readOnly: boolean;
  runner: GuideCtaRunner;
  onHide: (tip: GuideTipResponse, action: HidingAction) => void;
  /** The hub's deep-link target: scrolled to and ringed for two seconds. */
  focused?: boolean;
}

export function GuideTipCard({
  tip,
  variant,
  readOnly,
  runner,
  onHide,
  focused = false,
}: GuideTipCardProps): JSX.Element {
  const titleId = useId();
  const bodyId = useId();
  const [why, setWhy] = useState(false);
  const title = renderGuideText(tip.title, tip.values);
  const body = renderGuideText(tip.body, tip.values);
  const reason = tipWhy(tip);
  const onMenu = (action: TipMenuAction): void => onHide(tip, action);
  // One muted line under the title; a one-line clamp with the full text on
  // hover where a row is narrow. Plain text: a single number between words, a
  // grouped amount and a slashed date are all bidi-safe.
  const reasonLine = (className: string): JSX.Element | null =>
    reason ? (
      <TruncatedText as="span" className={className}>
        {plainGuideText(reason, tip.values)}
      </TruncatedText>
    ) : null;
  const dismiss = (
    <IconButton
      name="close"
      label={DISMISS_LABEL}
      size="small"
      type="text"
      disabledReason={readOnly ? READ_ONLY_REASON : null}
      onClick={() => onHide(tip, 'DISMISS')}
    />
  );
  const secondary = tip.secondaryCtas.filter(isRenderableCta).slice(0, 2);

  if (variant === 'inline') {
    return (
      <div className="bk-guide-inline" role="note" aria-labelledby={titleId}>
        <span className="bk-guide-inline__icon" aria-hidden="true">
          <Icon name="sparkle" size={16} />
        </span>
        <span className="bk-guide-inline__text">
          <span className="bk-guide-inline__title" id={titleId}>
            {title}
          </span>
          {reasonLine('bk-guide-reason')}
        </span>
        <CtaButton tip={tip} cta={tip.cta} runner={runner} appearance="link" />
        <GuideTipMenu tip={tip} readOnly={readOnly} onAction={onMenu} />
        {dismiss}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="bk-guide-compact">
        <span className="bk-guide-compact__icon" aria-hidden="true">
          <Icon name={iconOf(tip.icon)} size={20} />
        </span>
        <span className="bk-guide-compact__text">
          <span className="bk-guide-compact__title" id={titleId}>
            {title}
          </span>
          {reasonLine('bk-guide-reason')}
        </span>
        <span className="bk-guide-compact__actions">
          <CtaButton tip={tip} cta={tip.cta} runner={runner} appearance="link" />
          <GuideTipMenu tip={tip} readOnly={readOnly} onAction={onMenu} />
        </span>
      </div>
    );
  }

  if (variant === 'row') {
    return (
      <article
        className={['bk-guide-row', focused ? 'is-focused' : null].filter(Boolean).join(' ')}
        aria-labelledby={titleId}
        data-guide-tip={tip.itemKey}
        tabIndex={focused ? -1 : undefined}
      >
        <div className="bk-guide-row__head">
          <span className="bk-guide-row__icon" aria-hidden="true">
            <Icon name={iconOf(tip.icon)} size={20} />
          </span>
          <div className="bk-guide-row__main">
            <Eyebrow tip={tip} />
            <h6 className="bk-guide-row__title" id={titleId}>
              {title}
            </h6>
          </div>
          <GuideTipMenu tip={tip} readOnly={readOnly} onAction={onMenu} />
        </div>
        <p className="bk-guide-row__body">{body}</p>
        <div className="bk-guide-row__actions">
          <CtaButton tip={tip} cta={tip.cta} runner={runner} appearance="default" />
          {secondary.map((cta, index) => (
            <CtaButton
              key={`${cta.kind}-${index}`}
              tip={tip}
              cta={cta}
              runner={runner}
              appearance="link"
              secondary
            />
          ))}
        </div>
      </article>
    );
  }

  return (
    <article className="bk-guide-featured" aria-labelledby={titleId}>
      <div className="bk-guide-featured__top">
        <Eyebrow tip={tip} />
        <span className="bk-guide-featured__tools">
          <GuideTipMenu tip={tip} readOnly={readOnly} onAction={onMenu} />
          {dismiss}
        </span>
      </div>
      <h3 className="bk-guide-featured__title" id={titleId}>
        {title}
      </h3>
      {reason ? (
        <p className="bk-guide-reason bk-guide-featured__reason">
          {renderGuideText(reason, tip.values)}
        </p>
      ) : null}
      <p className="bk-guide-featured__body" id={bodyId} hidden={!why}>
        {body}
      </p>
      <div className="bk-guide-featured__actions">
        <CtaButton tip={tip} cta={tip.cta} runner={runner} appearance="default" size="middle" />
        {secondary.map((cta, index) => (
          <CtaButton
            key={`${cta.kind}-${index}`}
            tip={tip}
            cta={cta}
            runner={runner}
            appearance="link"
            secondary
          />
        ))}
        <Button
          type="link"
          size="small"
          className="bk-guide-link bk-guide-why"
          aria-expanded={why}
          aria-controls={bodyId}
          onClick={() => setWhy((open) => !open)}
        >
          {WHY_LABEL}
        </Button>
      </div>
    </article>
  );
}
